import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Fuel, Loader2, Play, Plus, Flag, Sparkles, Zap,
  ScrollText, Trophy, CheckCircle2, StopCircle, LayoutDashboard, Users,
  Coins, Skull, SlidersHorizontal, FlaskConical,
} from 'lucide-react';
import OilAdminDashboard from './oil/OilAdminDashboard';
import OilAdminLiveControl from './oil/OilAdminLiveControl';
import OilAdminPlayers from './oil/OilAdminPlayers';
import OilAdminEconomy from './oil/OilAdminEconomy';
import OilAdminMafia from './oil/OilAdminMafia';
import OilAdminConfig from './oil/OilAdminConfig';
import OilAdminAudit from './oil/OilAdminAudit';
import OilAdminTestTools from './oil/OilAdminTestTools';
import OilAdminTestAccounts from './oil/OilAdminTestAccounts';

const SECTIONS = [
  { id: 'dashboard', label: 'Visão do dia', icon: LayoutDashboard },
  { id: 'players', label: 'Jogadores', icon: Users },
  { id: 'economy', label: 'Economia', icon: Coins },
  { id: 'mafia', label: 'Máfia', icon: Skull },
  { id: 'config', label: 'Números da Season', icon: SlidersHorizontal },
  { id: 'test_accounts', label: 'Contas de teste', icon: FlaskConical },
  { id: 'audit', label: 'Auditoria', icon: ScrollText },
  { id: 'tools', label: 'Ferramentas de teste', icon: FlaskConical },
];

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

const GLOBAL_EVENTS = [
  { id: 'oil_boom', label: '🛢️ Boom de Petróleo (+% extração)' },
  { id: 'golden_hour', label: '💰 Hora Dourada (+% venda FOX)' },
  { id: 'dock_inspection', label: '🚔 Fiscalização nas Docas (bloqueia ataques)' },
];

export default function OilTycoonManagement() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [section, setSection] = useState('dashboard');
  const [services, setServices] = useState([]);
  const [evId, setEvId] = useState('oil_boom');
  const [evMin, setEvMin] = useState(60);
  const [evPct, setEvPct] = useState(20);
  const [prizeInfo, setPrizeInfo] = useState(null);
  const [payoutData, setPayoutData] = useState(null);
  const [prizeIn, setPrizeIn] = useState({ 1: 200000, 2: 100000, 3: 50000 });

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/oil/overview');
      setData(res.data);
      const active = res.data.seasons.find((s) => ['ACTIVE', 'FINALIZING', 'FINISHED'].includes(s.status));
      if (active) {
        const p = await api.get(`/admin/oil/season/${active.season_id}/wanted-prizes`);
        setPrizeInfo({ ...p.data, season_id: active.season_id });
        setPrizeIn(Object.fromEntries((p.data.prizes || []).map((x) => [x.rank, x.coins])));
        const po = await api.get(`/admin/oil/season/${active.season_id}/payouts`);
        setPayoutData({ ...po.data, season_id: active.season_id });
        try {
          const c = await api.get(`/admin/oil/season/${active.season_id}/config`);
          setServices(c.data.config?.mafia?.services || []);
        } catch { /* config opcional aqui */ }
      }
    } catch { toast.error('Erro ao carregar OIL TYCOON.'); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro na operação.'); }
    setBusy(false);
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;

  const seasons = data.seasons || [];
  const active = seasons.find((s) => s.status === 'ACTIVE');
  const draft = seasons.find((s) => ['DRAFT', 'SCHEDULED'].includes(s.status));
  const finished = seasons.find((s) => s.status === 'FINISHED');
  const stats = data.active_stats || {};
  const target = active || draft || finished || seasons[0];

  return (
    <div className="space-y-4" data-testid="oil-admin">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Fuel className="w-5 h-5 text-orange-500" /> OIL TYCOON — Season
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {!active && !draft && (
              <Button size="sm" disabled={busy} data-testid="oil-create-season"
                onClick={() => act(() => api.post('/admin/oil/season', { duration_days: 30 }), 'Season criada (DRAFT)!')}
                className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="w-4 h-4 mr-1" /> Criar nova Season
              </Button>
            )}
            {draft && (
              <Button size="sm" disabled={busy} data-testid="oil-activate-season"
                onClick={() => window.confirm('ATIVAR a Season agora? O jogo abre para todos os clientes.') &&
                  act(() => api.post(`/admin/oil/season/${draft.season_id}/activate`), 'Season ATIVA! 🛢️')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Play className="w-4 h-4 mr-1" /> Ativar Season
              </Button>
            )}
            {active && (
              <Button size="sm" variant="destructive" disabled={busy} data-testid="oil-finalize-season"
                onClick={() => window.confirm('FINALIZAR a Season? Rankings serão congelados e o payout calculado. Irreversível!') &&
                  act(() => api.post(`/admin/oil/season/${active.season_id}/finalize`), 'Season finalizada! Veja os resultados.')}>
                <Flag className="w-4 h-4 mr-1" /> Finalizar Season
              </Button>
            )}
            {finished && (
              <Button size="sm" disabled={busy} data-testid="oil-mark-paid"
                onClick={() => act(() => api.post(`/admin/oil/season/${finished.season_id}/mark-paid`), 'Season marcada como PAGA.')}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como paga
              </Button>
            )}
          </div>

          {seasons.length === 0 && <p className="text-sm text-slate-400">Nenhuma Season criada ainda.</p>}
          {target && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl border p-3"><p className="text-[10px] uppercase text-slate-400">Status</p>
                <p className="font-black" data-testid="oil-season-status">{target.status}</p></div>
              <div className="rounded-xl border p-3"><p className="text-[10px] uppercase text-slate-400">Jogadores</p>
                <p className="font-black">{fmt(stats.players)}</p></div>
              <div className="rounded-xl border p-3"><p className="text-[10px] uppercase text-slate-400">Jackpot Magnatas</p>
                <p className="font-black text-amber-600">🪙 {fmt(stats.jackpot?.magnates ?? target.jackpot?.magnates)}</p></div>
              <div className="rounded-xl border p-3"><p className="text-[10px] uppercase text-slate-400">Jackpot Acionistas</p>
                <p className="font-black text-emerald-600">🪙 {fmt(stats.jackpot?.shareholders ?? target.jackpot?.shareholders)}</p></div>
            </div>
          )}
        </CardContent>
      </Card>

      {active && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="w-4 h-4 text-amber-500" /> Eventos Globais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <select value={evId} onChange={(e) => setEvId(e.target.value)} data-testid="oil-event-select"
                className="h-9 rounded-lg border px-2 text-sm bg-white">
                {GLOBAL_EVENTS.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
              </select>
              <input type="number" value={evMin} onChange={(e) => setEvMin(+e.target.value)} min={5} max={1440}
                className="h-9 w-24 rounded-lg border px-2 text-sm" title="Duração (min)" data-testid="oil-event-minutes" />
              <input type="number" value={evPct} onChange={(e) => setEvPct(+e.target.value)} min={0} max={200}
                className="h-9 w-20 rounded-lg border px-2 text-sm" title="Bônus %" data-testid="oil-event-pct" />
              <Button size="sm" disabled={busy} data-testid="oil-event-start"
                onClick={() => act(() => api.post(`/admin/oil/season/${active.season_id}/global-event`,
                  { event_id: evId, duration_minutes: evMin, bonus_pct: evPct }), 'Evento global iniciado!')}
                className="bg-amber-500 hover:bg-amber-600 text-white">
                <Sparkles className="w-4 h-4 mr-1" /> Iniciar
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(active.active_global_events || []).filter((e) => e.ends_at > new Date().toISOString()).map((e) => (
                <span key={e.id} className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-xs font-bold text-amber-700">
                  {e.id} +{e.bonus_pct}% até {fmtDate(e.ends_at)}
                  <button onClick={() => act(() => api.delete(`/admin/oil/season/${active.season_id}/global-event/${e.id}`), 'Evento encerrado.')}>
                    <StopCircle className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {prizeInfo && (
        <Card data-testid="oil-wanted-prize-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="w-4 h-4 text-emerald-600" /> Prêmio dos Mais Procurados (Top 3)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">
              Moedas KAKO para o Top 3 dos Procurados. Os ganhadores são salvos na finalização
              e o crédito só sai depois que você aprovar no card de pagamentos abaixo.
            </p>
            <div className="flex flex-wrap items-end gap-2">
              {[1, 2, 3].map((r) => (
                <label key={r} className="text-xs font-bold text-slate-600">
                  <span className="block">{r}º lugar</span>
                  <input type="number" min={0} max={1000000} value={prizeIn[r] ?? 0}
                    data-testid={`oil-prize-input-${r}`}
                    onChange={(e) => setPrizeIn((p) => ({ ...p, [r]: +e.target.value }))}
                    className="mt-1 h-9 w-28 rounded-lg border px-2 text-sm" />
                </label>
              ))}
              <Button size="sm" disabled={busy} data-testid="oil-prize-save"
                onClick={() => act(() => api.patch(
                  `/admin/oil/season/${prizeInfo.season_id}/wanted-prizes`,
                  { prizes: [1, 2, 3].map((r) => ({ rank: r, coins: +prizeIn[r] || 0 })) }),
                  'Prêmios atualizados!')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Salvar prêmios
              </Button>
            </div>

            {prizeInfo.pending_hint && null}
          </CardContent>
        </Card>
      )}

      {payoutData && (payoutData.pending > 0 || Object.values(payoutData.groups || {}).some((g) => g.length)) && (
        <Card data-testid="oil-payouts-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Trophy className="w-4 h-4 text-amber-600" /> Pagamentos dos rankings — liberação manual
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-bold text-amber-800">
              Nada é creditado automaticamente. Os ganhadores ficam salvos aqui e as moedas só
              saem da sua conta KAKO quando você aprovar.
            </p>
            {Object.entries(payoutData.groups || {}).map(([kind, rows]) => rows.length > 0 && (
              <div key={kind} className="space-y-1" data-testid={`oil-payout-group-${kind}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase text-slate-600">
                    {payoutData.kinds[kind]} · {fmt(payoutData.totals[kind].coins)} moedas ·
                    {' '}{payoutData.totals[kind].pending} a liberar
                  </p>
                  {payoutData.totals[kind].pending > 0 && (
                    <Button size="sm" disabled={busy} data-testid={`oil-payout-approve-all-${kind}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => {
                        if (!window.confirm(`Liberar TODOS os prêmios de ${payoutData.kinds[kind]}? As moedas serão creditadas de verdade.`)) return;
                        act(() => api.post(`/admin/oil/season/${payoutData.season_id}/payouts/${kind}/approve-all`), 'Prêmios liberados!');
                      }}>
                      Aprovar e creditar todos
                    </Button>
                  )}
                </div>
                {rows.map((p) => (
                  <div key={`${kind}-${p.rank}`} data-testid={`oil-payout-${kind}-${p.rank}`}
                    className="flex flex-wrap items-center gap-2 rounded-lg border p-2 text-xs">
                    <b className="w-8">{p.rank}º</b>
                    <span className="text-slate-600">{p.nickname || '—'} · KAKO {p.kako_id}</span>
                    <b className="text-emerald-700">{fmt(p.coins)} moedas</b>
                    <span className={`rounded-full px-2 py-0.5 font-bold ${
                      p.status === 'PAID' ? 'bg-emerald-100 text-emerald-700'
                        : p.status === 'FAILED' ? 'bg-rose-100 text-rose-700'
                          : 'bg-slate-100 text-slate-600'}`}>
                      {p.status === 'AWAITING_APPROVAL' ? 'AGUARDANDO SUA APROVAÇÃO' : p.status}
                    </span>
                    {p.message && <span className="text-slate-400">{p.message}</span>}
                    {p.status !== 'PAID' && (
                      <Button size="sm" variant="outline" disabled={busy}
                        data-testid={`oil-payout-approve-${kind}-${p.rank}`}
                        onClick={() => {
                          if (!window.confirm(`Creditar ${p.coins} moedas para KAKO ${p.kako_id}?`)) return;
                          act(() => api.post(`/admin/oil/season/${payoutData.season_id}/payouts/${kind}/${p.rank}/approve`), 'Prêmio creditado!');
                        }}>
                        {p.status === 'FAILED' ? 'Tentar de novo' : 'Aprovar e creditar'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {target && (
        <Card data-testid="oil-admin-panel">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap gap-1" data-testid="oil-admin-sections">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => setSection(s.id)} data-testid={`oil-admin-tab-${s.id}`}
                    className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                      section === s.id ? 'border-orange-400 bg-orange-50 text-orange-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <Icon className="h-3.5 w-3.5" /> {s.label}
                  </button>
                );
              })}
            </div>
          </CardHeader>
          <CardContent>
            {section === 'dashboard' && (
              <div className="space-y-3">
                <OilAdminLiveControl seasonId={target.season_id} onChanged={load} />
                <OilAdminDashboard seasonId={target.season_id} />
              </div>
            )}
            {section === 'players' && <OilAdminPlayers seasonId={target.season_id} />}
            {section === 'economy' && <OilAdminEconomy seasonId={target.season_id} />}
            {section === 'mafia' && <OilAdminMafia seasonId={target.season_id} services={services} />}
            {section === 'config' && <OilAdminConfig seasonId={target.season_id} onSaved={load} />}
            {section === 'test_accounts' && <OilAdminTestAccounts />}
            {section === 'audit' && <OilAdminAudit seasonId={target.season_id} />}
            {section === 'tools' && <OilAdminTestTools seasonId={target.season_id} />}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
